import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import SellerLayout from './SellerLayout';

/**
 * SellerSmartfarmForm.jsx
 *
 * 연결 API
 * GET  /api/seller/smartfarm/form-data?id=1
 * POST /api/seller/smartfarm/test-connection
 * POST /api/seller/smartfarm/test-mycodo-connection
 * POST /api/seller/smartfarm/save-process
 * GET  /api/seller/smartfarm/load-entities?id=1
 * POST /api/seller/smartfarm/entities-save
 * POST /api/seller/smartfarm/dashboard/create
 *
 * 라우터 예시
 * <Route path="/seller/smartfarm/add" element={<SellerSmartfarmForm />} />
 * <Route path="/seller/smartfarm/edit" element={<SellerSmartfarmForm />} />
 */
const SellerSmartfarmForm = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const siteId = Number(query.get('id') || 0);
  const presetMemberId = String(query.get('member_id') || '').trim();
  const isEdit = siteId > 0;

  const api = useMemo(
    () =>
      axios.create({
        withCredentials: true,
      }),
    []
  );

  const [shopName, setShopName] = useState('');
  const [members, setMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState('');

  const [connectionType, setConnectionType] = useState('HA');
  const [connectionPanel, setConnectionPanel] = useState('HA');

  const [form, setForm] = useState({
    member_id: '',
    site_name: '',
    ha_base_url: '',
    ha_token: '',
    mycodo_base_url: '',
    mycodo_api_key: '',
    mycodo_function_id: '',
    is_active: 'Y',
  });

  const [tokenSaved, setTokenSaved] = useState(false);
  const [tokenMasked, setTokenMasked] = useState('');
  const [mycodoApiKeySaved, setMycodoApiKeySaved] = useState(false);
  const [mycodoApiKeyMasked, setMycodoApiKeyMasked] = useState('');

  const [entities, setEntities] = useState([]);
  const [cameraConfigs, setCameraConfigs] = useState({});
  const [cameraConfigBusy, setCameraConfigBusy] = useState('');
  const [cameraJobStatus, setCameraJobStatus] = useState({});
  const [onvifSearching, setOnvifSearching] = useState(false);
  const [onvifDevices, setOnvifDevices] = useState([]);
  const [cctvDraftIds, setCctvDraftIds] = useState([]);
  const [onvifCameraIps, setOnvifCameraIps] = useState({});
  const [cctvPair, setCctvPair] = useState({ worker_site_id: '', agent_token: '', public_url: '' });
  const [cctvPaired, setCctvPaired] = useState(false);
  const [cctvOnline, setCctvOnline] = useState(false);
  const [cctvPairBusy, setCctvPairBusy] = useState(false);
  const [cctvIssuedToken, setCctvIssuedToken] = useState('');
  const [cctvPendingActivation, setCctvPendingActivation] = useState(null);
  const [entitySearch, setEntitySearch] = useState('');
  const [domainFilter, setDomainFilter] = useState('ALL');

  const [pageLoading, setPageLoading] = useState(true);
  const [connectionTesting, setConnectionTesting] = useState(false);
  const [mycodoConnectionTesting, setMycodoConnectionTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entitySaving, setEntitySaving] = useState(false);
  const [dashboardCreating, setDashboardCreating] = useState(false);
  const [dashboardResult, setDashboardResult] = useState(null);

  const [connectionResult, setConnectionResult] = useState(null);
  const [mycodoConnectionResult, setMycodoConnectionResult] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(null);
  const [updateBusy, setUpdateBusy] = useState(false);
  const refreshUpdateStatus = useCallback(async () => {
    if (!siteId) return;
    try {
      const { data } = await api.get('/api/seller/smartfarm/update-agent/status', { params: { site_id: siteId } });
      if (data.ok) setUpdateStatus(data);
    } catch (err) { console.warn('Update status unavailable:', err?.response?.status); }
  }, [api, siteId]);
  useEffect(() => {
    if (!updateModalOpen) return undefined;
    refreshUpdateStatus();
    const timer = window.setInterval(refreshUpdateStatus, 5000);
    return () => window.clearInterval(timer);
  }, [updateModalOpen, refreshUpdateStatus]);
  const [updatePairBusy, setUpdatePairBusy] = useState(false);
  const [updatePendingActivation, setUpdatePendingActivation] = useState(null);
  const registerUpdateWorker = async () => {
    setUpdatePairBusy(true); setError('');
    try {
      const issued = updatePendingActivation || (await api.post('/api/seller/smartfarm/update-agent/issue-token', {
        site_id: siteId, public_url: physicalCctvUrl,
      })).data;
      if (!issued?.ok || !issued.agent_token) throw new Error(issued?.error || '토큰 발급 실패');
      setUpdatePendingActivation(issued);
      const res = await fetch(`${issued.public_url}/__update_activate`, {
        method: 'POST', mode: 'cors', cache: 'no-store',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agent_token: issued.agent_token, worker_site_id: issued.worker_site_id }),
      });
      const activation = await res.json().catch(() => ({}));
      if (!res.ok || !activation.ok) throw new Error(activation.error || `현장 활성화 실패 (${res.status})`);
      const { data } = await api.post('/api/seller/smartfarm/update-agent/confirm-activation', {
        site_id: siteId, agent_token: issued.agent_token,
      });
      if (!data?.ok) throw new Error(data?.error || '중앙 등록 확정 실패');
      setUpdatePendingActivation(null);
      setNotice('업데이트 Worker 인증 완료');
      await refreshUpdateStatus();
    } catch (err) { setError(err?.response?.data?.error || err?.message || '업데이트 Worker 등록 실패'); }
    finally { setUpdatePairBusy(false); }
  };
  const requestCloudflaredUpdate = async () => {
    if (!window.confirm('이 농장의 Cloudflare Tunnel을 업데이트할까요? 연결이 잠시 중단될 수 있습니다.')) return;
    setUpdateBusy(true);
    try {
      const { data } = await api.post('/api/seller/smartfarm/update-agent/request', { site_id: siteId });
      if (!data.ok) throw new Error(data.error || '업데이트 요청 실패');
      setNotice(`업데이트 작업 #${data.job_id} 등록 완료`);
      await refreshUpdateStatus();
    } catch (err) { setError(err?.response?.data?.error || err?.message || '업데이트 요청 실패'); }
    finally { setUpdateBusy(false); }
  };

  const isOnvifCctvEntity = (id) => /^camera\.onvif_[0-9a-f]{8}$/i.test(String(id || ''));

  const normalizeEntity = (entity, index = 0) => ({
    id: entity.id || null,
    entity_id: entity.entity_id || '',
    entity_name:
      entity.entity_name ||
      entity.friendly_name ||
      entity.entity_id ||
      '',
    entity_type: entity.entity_type || '',
    domain:
      entity.domain ||
      (entity.entity_id?.includes('.')
        ? entity.entity_id.split('.', 1)[0]
        : ''),
    icon: entity.icon || '',
    state: entity.state,
    unit_of_measurement: entity.unit_of_measurement || '',
    last_changed: entity.last_changed || '',
    last_updated: entity.last_updated || '',
    sort_order:
      Number.isFinite(Number(entity.sort_order))
        ? Number(entity.sort_order)
        : index,
    is_visible: entity.is_visible || 'Y',
    selected:
      typeof entity.selected === 'boolean'
        ? entity.selected
        : entity.is_visible !== 'N',
  });

  const loadFormData = useCallback(async () => {
    setPageLoading(true);
    setError('');

    try {
      const response = await api.get('/api/seller/smartfarm/form-data', {
        params: isEdit ? { id: siteId } : {},
      });

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '스마트팜 정보를 불러오지 못했습니다.');
      }

      setShopName(data.shop_name || '');
      setMembers(Array.isArray(data.members) ? data.members : []);

      if (!isEdit && presetMemberId) {
        setForm((prev) => ({
          ...prev,
          member_id: presetMemberId,
        }));
      }

      if (data.site) {
        const nextConnectionType = String(
          data.site.connection_type || 'HA'
        ).toUpperCase();

        setConnectionType(nextConnectionType);
        setConnectionPanel(nextConnectionType === 'DIRECT' ? 'DIRECT' : 'HA');

        setForm({
          member_id: String(data.site.member_id || ''),
          site_name: data.site.site_name || '',
          ha_base_url: data.site.ha_base_url || '',
          ha_token: '',
          mycodo_base_url: data.site.mycodo_base_url || '',
          mycodo_api_key: '',
          mycodo_function_id: data.site.mycodo_function_id || '',
          is_active: data.site.is_active || 'Y',
        });

        setTokenSaved(Boolean(data.site.token_saved));
        setTokenMasked(data.site.token_masked || '');
        setMycodoApiKeySaved(Boolean(data.site.mycodo_api_key_saved));
        setMycodoApiKeyMasked(data.site.mycodo_api_key_masked || '');
        setConnectionResult({
          connected: data.site.connection_status === 'CONNECTED',
          message:
            data.site.connection_status === 'CONNECTED'
              ? '현재 Home Assistant 연결 상태가 정상입니다.'
              : '현재 Home Assistant 연결 상태를 확인해야 합니다.',
        });
      }

      if (Array.isArray(data.entities)) {
        setEntities(
          data.entities.filter(entity => !isOnvifCctvEntity(entity.entity_id)).map((entity, index) =>
            normalizeEntity(
              {
                ...entity,
                selected: entity.is_visible !== 'N',
              },
              index
            )
          )
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      const message =
        err?.response?.data?.error ||
        err?.message ||
        '스마트팜 정보를 불러오지 못했습니다.';

      if (status === 401) {
        navigate('/seller/manage', { replace: true });
        return;
      }

      setError(message);
    } finally {
      setPageLoading(false);
    }
  }, [api, isEdit, navigate, siteId, presetMemberId]);

  useEffect(() => {
    loadFormData();
  }, [loadFormData]);

  const filteredMembers = useMemo(() => {
    const keyword = memberSearch.trim().toLowerCase();

    if (!keyword) return members;

    return members.filter((member) => {
      const haystack = [
        member.nickname,
        member.email,
        member.phone,
        member.social,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(keyword);
    });
  }, [memberSearch, members]);

  const domains = useMemo(() => {
    const values = new Set(
      entities
        .map((entity) => entity.domain)
        .filter(Boolean)
    );

    return Array.from(values).sort();
  }, [entities]);

  const visibleEntities = useMemo(() => {
    const keyword = entitySearch.trim().toLowerCase();

    return entities.filter((entity) => {
      if (
        domainFilter !== 'ALL' &&
        entity.domain !== domainFilter
      ) {
        return false;
      }

      if (!keyword) return true;

      return [
        entity.entity_name,
        entity.entity_id,
        entity.domain,
        entity.entity_type,
        entity.state,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(keyword);
    });
  }, [domainFilter, entities, entitySearch]);

  const selectedCount = useMemo(
    () => entities.filter((entity) => entity.selected).length,
    [entities]
  );

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'ha_base_url' || name === 'ha_token') {
      setConnectionResult(null);
    }

    if (
      name === 'mycodo_base_url' ||
      name === 'mycodo_api_key' ||
      name === 'mycodo_function_id'
    ) {
      setMycodoConnectionResult(null);
    }
  };

  const validateBasicForm = () => {
    if (!form.member_id) {
      setError('회원을 선택하세요.');
      return false;
    }

    if (!form.site_name.trim()) {
      setError('농장명을 입력하세요.');
      return false;
    }

    if (connectionType === 'HA') {
      if (!form.ha_base_url.trim()) {
        setError('Home Assistant 주소를 입력하세요.');
        return false;
      }

      if (!/^https?:\/\//i.test(form.ha_base_url.trim())) {
        setError('Home Assistant 주소는 http:// 또는 https://로 시작해야 합니다.');
        return false;
      }

      if (!isEdit && !form.ha_token.trim()) {
        setError('신규 등록 시 Home Assistant 토큰을 입력하세요.');
        return false;
      }
    }

    return true;
  };

  const handleTestConnection = async () => {
    setError('');
    setNotice('');
    setConnectionTesting(true);

    try {
      if (!form.ha_base_url.trim()) {
        throw new Error('Home Assistant 주소를 입력하세요.');
      }

      if (!isEdit && !form.ha_token.trim()) {
        throw new Error('Home Assistant 토큰을 입력하세요.');
      }

      const payload = {
        id: isEdit ? siteId : undefined,
        ha_base_url: form.ha_base_url.trim(),
        ha_token: form.ha_token.trim(),
      };

      const response = await api.post(
        '/api/seller/smartfarm/test-connection',
        payload
      );

      const data = response.data || {};

      if (!data.ok || !data.connected) {
        throw new Error(data.error || 'Home Assistant 연결에 실패했습니다.');
      }

      setConnectionResult({
        connected: true,
        message:
          data.message ||
          'Home Assistant 연결에 성공했습니다.',
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Home Assistant 연결에 실패했습니다.';

      setConnectionResult({
        connected: false,
        message,
      });
      setError(message);
    } finally {
      setConnectionTesting(false);
    }
  };

  const handleTestMycodoConnection = async () => {
    setError('');
    setNotice('');
    setMycodoConnectionTesting(true);

    try {
      if (!form.mycodo_base_url.trim()) {
        throw new Error('Mycodo 주소를 입력하세요.');
      }

      if (!/^https?:\/\//i.test(form.mycodo_base_url.trim())) {
        throw new Error('Mycodo 주소는 http:// 또는 https://로 시작해야 합니다.');
      }

      if (!form.mycodo_function_id.trim()) {
        throw new Error('Mycodo Function ID를 입력하세요.');
      }

      if (!form.mycodo_api_key.trim() && !mycodoApiKeySaved) {
        throw new Error('Mycodo API Key를 입력하세요.');
      }

      const payload = {
        id: isEdit ? siteId : undefined,
        mycodo_base_url: form.mycodo_base_url.trim(),
        mycodo_api_key: form.mycodo_api_key.trim(),
        mycodo_function_id: form.mycodo_function_id.trim(),
      };

      const response = await api.post(
        '/api/seller/smartfarm/test-mycodo-connection',
        payload
      );

      const data = response.data || {};

      if (!data.ok || !data.connected) {
        throw new Error(
          data.error || 'Mycodo 연결에 실패했습니다.'
        );
      }

      setMycodoConnectionResult({
        connected: true,
        message:
          data.message ||
          'Mycodo 연결과 Function 확인에 성공했습니다.',
      });
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Mycodo 연결에 실패했습니다.';

      setMycodoConnectionResult({
        connected: false,
        message,
      });
      setError(message);
    } finally {
      setMycodoConnectionTesting(false);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setError('');
    setNotice('');

    if (!validateBasicForm()) return;

    setSaving(true);

    try {
      const response = await api.post(
        '/api/seller/smartfarm/save-process',
        {
          id: isEdit ? siteId : undefined,
          member_id: String(form.member_id || '').trim(),
          site_name: form.site_name.trim(),
          connection_type: connectionType,
          ha_base_url: form.ha_base_url.trim(),
          ha_token: form.ha_token.trim(),
          mycodo_base_url: form.mycodo_base_url.trim(),
          mycodo_api_key: form.mycodo_api_key.trim(),
          mycodo_function_id: form.mycodo_function_id.trim(),
          is_active: form.is_active,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '스마트팜 저장에 실패했습니다.');
      }

      const savedId = Number(data.id || siteId);

      setNotice(data.message || '스마트팜이 저장되었습니다.');
      setConnectionResult({
        connected: Boolean(data.connected),
        message:
          data.connection_status === 'CONNECTED'
            ? 'Home Assistant 연결 상태가 정상입니다.'
            : '농장 정보는 저장되었지만 Home Assistant 연결을 확인해야 합니다.',
      });

      if (form.ha_token.trim() || tokenSaved) {
        setTokenSaved(true);
        setTokenMasked('저장됨');
      }

      if (form.mycodo_api_key.trim() || mycodoApiKeySaved) {
        setMycodoApiKeySaved(true);
        setMycodoApiKeyMasked('저장됨');
      }

      setForm((prev) => ({
        ...prev,
        ha_token: '',
        mycodo_api_key: '',
      }));

      if (!isEdit && savedId > 0) {
        navigate(`/seller/smartfarm/edit?id=${savedId}`, {
          replace: true,
          state: {
            message: '농장이 등록되었습니다. 이제 엔티티를 불러와 선택하세요.',
          },
        });
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        '스마트팜 저장에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const handleLoadEntities = async () => {
    if (!isEdit || siteId <= 0) {
      setError('농장 정보를 먼저 저장한 후 엔티티를 불러오세요.');
      return;
    }

    setError('');
    setNotice('');
    setEntityLoading(true);

    try {
      const response = await api.get(
        '/api/seller/smartfarm/load-entities',
        {
          params: { id: siteId },
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '엔티티를 불러오지 못했습니다.');
      }

      const nextEntities = Array.isArray(data.entities)
        ? data.entities.map((entity, index) =>
            normalizeEntity(entity, index)
          )
        : [];

      setEntities(nextEntities);
      setConnectionResult({
        connected: true,
        message: 'Home Assistant 엔티티를 정상적으로 불러왔습니다.',
      });
      setNotice(`총 ${nextEntities.length}개의 엔티티를 불러왔습니다.`);
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        '엔티티를 불러오지 못했습니다.';

      setConnectionResult({
        connected: false,
        message,
      });
      setError(message);
    } finally {
      setEntityLoading(false);
    }
  };

  const loadCameraConfigs = useCallback(async () => {
    if (!isEdit || siteId <= 0) return;
    try {
      const response = await api.get('/api/seller/smartfarm/camera-configs', {
        params: { site_id: siteId },
      });
      if (!response.data?.ok) return;
      const configs = {};
      (response.data.cameras || []).forEach((camera) => {
        configs[camera.entity_id] = camera;
      });
      setCameraConfigs(configs);
    } catch (err) {
      // 카메라 테이블 미생성 시 기존 HA 엔티티 등록을 방해하지 않는다.
      console.warn('Camera mapping unavailable:', err?.response?.status || err?.message);
    }
  }, [api, isEdit, siteId]);

  useEffect(() => {
    loadCameraConfigs();
  }, [loadCameraConfigs]);

  useEffect(() => {
    if (!isEdit || siteId <= 0) return;
    let active = true;
    api.get('/api/seller/smartfarm/cctv-agent/pair', { params: { site_id: siteId } })
      .then(({data}) => { if (active && data?.ok) {
        setCctvPaired(Boolean(data.paired));
        setCctvOnline(Boolean(data.online));
        setCctvPair(prev => ({ ...prev, worker_site_id: data.worker_site_id || '',
          public_url: data.public_url || '' }));
      }})
      .catch(() => {});
    return () => { active = false; };
  }, [api, isEdit, siteId]);

  useEffect(() => {
    if (!isEdit || !siteId || !cctvPaired) return undefined;
    let active = true;
    const refresh = () => api.get('/api/seller/smartfarm/cctv-agent/pair', { params: { site_id: siteId } })
      .then(({ data }) => { if (active && data?.ok) setCctvOnline(Boolean(data.online)); })
      .catch(() => { if (active) setCctvOnline(false); });
    refresh();
    const timer = window.setInterval(refresh, 10000);
    return () => { active = false; window.clearInterval(timer); };
  }, [api, isEdit, siteId, cctvPaired]);

  const physicalCctvUrl = useMemo(() => {
    const numbers = [form.ha_base_url, form.mycodo_base_url]
      .map(url => {
        try { return new URL(url).hostname.match(/^(?:smartfarm|mycodo)-([1-9][0-9]*)\.konggomul\.com$/)?.[1] || null; }
        catch { return null; }
      }).filter(Boolean);
    return numbers.length && numbers.every(n => n === numbers[0])
      ? `https://cctv-${numbers[0]}.konggomul.com` : '';
  }, [form.ha_base_url, form.mycodo_base_url]);

  const activateCctvWorker = async (issued) => {
    const activation = await fetch(`${issued.public_url}/__cctv_activate`, {
      method: 'POST', mode: 'cors', cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ agent_token: issued.agent_token, worker_site_id: issued.worker_site_id }),
    });
    const activationResult = await activation.json().catch(() => ({}));
    if (!activation.ok || !activationResult.ok) throw new Error(activationResult.error || `현장 활성화 실패 (${activation.status})`);
    const { data: confirmation } = await api.post('/api/seller/smartfarm/cctv-agent/confirm-activation', {
      site_id: siteId, agent_token: issued.agent_token,
    });
    if (!confirmation?.ok) throw new Error(confirmation?.error || '중앙 서버 연결 확정 실패');
    setCctvPendingActivation(null);
    setCctvIssuedToken('');
    setCctvPaired(true);
    setCctvOnline(false);
    setCctvPair(prev => ({ ...prev, worker_site_id: issued.worker_site_id, public_url: issued.public_url, agent_token: '' }));
    setNotice(`CCTV Worker ${issued.worker_site_id} 자동 활성화 완료. 온라인 상태를 확인 중입니다.`);
  };

  const issueCctvWorkerToken = async () => {
    if (!isEdit || siteId <= 0) return;
    if (cctvPaired && !cctvPendingActivation && !window.confirm(
      cctvOnline
        ? '현재 CCTV Worker가 온라인입니다. 교체하면 기존 Worker 인증이 변경되고 CCTV 연결이 잠시 끊길 수 있습니다. 교체하시겠습니까?'
        : '기존 CCTV Worker를 교체하시겠습니까?'
    )) return;
    setError(''); setNotice(''); setCctvPairBusy(true);
    try {
      if (cctvPendingActivation) {
        await activateCctvWorker(cctvPendingActivation);
        return;
      }
      if (!physicalCctvUrl) throw new Error('HA/Mycodo 주소의 smartfarm 번호를 확인하세요.');
      // 같은 공개 주소의 기존 Worker 재설치도 중앙 서버의 승인 정책에 따라 처리한다.
      const { data } = await api.post('/api/seller/smartfarm/cctv-agent/issue-token', {
        site_id: siteId, public_url: physicalCctvUrl,
      });
      if (!data?.ok || !data.agent_token) throw new Error(data?.error || '토큰 발급 실패');
      setCctvPendingActivation(data);
      await activateCctvWorker(data);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'CCTV Worker 자동 등록 실패');
    } finally { setCctvPairBusy(false); }
  };

  const pairCctvWorker = async () => {
    setCctvPairBusy(true);
    setError('');
    try {
      const { data } = await api.post('/api/seller/smartfarm/cctv-agent/pair', {
        site_id: siteId,
        agent_token: cctvPair.agent_token, public_url: cctvPair.public_url,
      });
      if (!data?.ok) throw new Error(data?.error || '현장 연결 실패');
      setCctvPaired(true);
      setCctvOnline(false);
      setCctvPair(prev => ({ ...prev, agent_token: '' }));
      setNotice(data.message || '현장 CCTV 연결 완료');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || '현장 CCTV 연결 실패');
    } finally { setCctvPairBusy(false); }
  };

  const saveCameraConfig = async (entity) => {
    const config = cameraConfigs[entity.entity_id] || {};
    setCameraConfigBusy(entity.entity_id);
    setError('');
    try {
      const response = await api.post('/api/seller/smartfarm/camera-configs', {
        site_id: siteId,
        entity_id: entity.entity_id,
        camera_name: entity.entity_name || entity.entity_id,
        stream_name: config.stream_name || '',
        public_url: config.public_url || '',
        is_active: config.is_active || 'Y',
      });
      if (!response.data?.ok) throw new Error(response.data?.error || '저장 실패');
      setNotice(response.data.message || '카메라 연결 정보가 저장되었습니다.');
      await loadCameraConfigs();
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || '카메라 설정 저장 실패');
    } finally {
      setCameraConfigBusy('');
    }
  };

  const saveCameraDisplayName = async (entityId) => {
    setCameraConfigBusy(entityId);
    setError('');
    try {
      const { data } = await api.post('/api/seller/smartfarm/camera-display-name', {
        site_id: siteId, entity_id: entityId,
        display_name: cameraConfigs[entityId]?.display_name || '',
      });
      if (!data?.ok) throw new Error(data?.error || '표시 이름 저장 실패');
      setNotice('카메라 표시 이름이 저장되었습니다.');
      await loadCameraConfigs();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || '표시 이름 저장 실패');
    } finally {
      setCameraConfigBusy('');
    }
  };

  const deleteCameraConfig = async (entity) => {
    const config = cameraConfigs[entity.entity_id];
    if (!config) return;
    if (!window.confirm(`카메라 "${entity.entity_name || entity.entity_id}"를 현장 go2rtc에서도 완전히 삭제할까요?`)) return;
    setCameraConfigBusy(entity.entity_id);
    setError('');
    try {
      const { data } = await api.post('/api/seller/smartfarm/camera-configs/delete', {
        site_id: siteId, entity_id: entity.entity_id,
      });
      if (!data?.ok) throw new Error(data?.error || '카메라 등록 해제 실패');
      setCameraJobStatus(prev => ({ ...prev, [entity.entity_id]: '현장 삭제 중...' }));
      let complete = false;
      for (let i = 0; i < 18; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const status = (await api.get(`/api/seller/smartfarm/cctv-register/${data.job_id}`)).data;
        if (status.status === 'done') {
          setCameraJobStatus(prev => ({ ...prev, [entity.entity_id]: '삭제 완료' }));
          setNotice('현장 go2rtc 및 중앙 서버 카메라 등록 해제 완료');
          setCctvDraftIds(prev => prev.filter(id => id !== entity.entity_id));
          await loadCameraConfigs();
          complete = true;
          break;
        }
        if (status.status === 'failed') throw new Error(status.error || '현장 카메라 삭제 실패');
      }
      if (!complete) setCameraJobStatus(prev => ({ ...prev, [entity.entity_id]: '삭제 처리 중. 잠시 후 다시 확인하세요.' }));
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || '카메라 등록 해제 실패');
    } finally {
      setCameraConfigBusy('');
    }
  };

  const searchOnvifCameras = async () => {
    setOnvifSearching(true);
    setError('');
    setOnvifDevices([]);
    try {
      const { data } = await api.post('/api/seller/smartfarm/cctv-onvif/search', { site_id: siteId });
      if (!data?.ok) throw new Error(data?.error || 'ONVIF 검색 요청 실패');
      // 검색할 때마다 중앙 서버의 최신 등록 정보를 조회한다.
      // 조회 실패 시 이미 등록된 카메라를 다시 보여주지 않도록 검색을 중단한다.
      const configResponse = await api.get('/api/seller/smartfarm/camera-configs', {
        params: { site_id: siteId },
      });
      if (!configResponse.data?.ok || !Array.isArray(configResponse.data.cameras)) {
        throw new Error('등록된 CCTV 목록을 확인하지 못했습니다. 다시 시도하세요.');
      }
      const registeredCameras = configResponse.data.cameras.filter(camera =>
        String(camera.public_url || '').trim()
      );
      const registeredEntityIds = new Set(registeredCameras.map(camera => camera.entity_id));
      const registeredStreamNames = new Set(registeredCameras.map(camera => camera.stream_name).filter(Boolean));
      const existingDraftIds = new Set(cctvDraftIds);
      let finished = false;
      for (let i = 0; i < 20; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const result = (await api.get(`/api/seller/smartfarm/cctv-onvif/search/${data.job_id}`)).data;
        if (result.status === 'discover_done') {
          const availableDevices = (Array.isArray(result.devices) ? result.devices : []).filter(device => {
            if (!device?.ip || !/^(?:\d{1,3}\.){3}\d{1,3}$/.test(device.ip)) return false;
            const entityId = onvifEntity(device).entity_id;
            const defaultStreamName = `onvif_${device.ip.replace(/\./g, '_')}`;
            return !registeredEntityIds.has(entityId)
              && !registeredStreamNames.has(defaultStreamName)
              && !existingDraftIds.has(entityId);
          });
          setOnvifDevices(availableDevices);
          setNotice(`ONVIF 신규 등록 가능 카메라 ${availableDevices.length}대 검색 완료`);
          finished = true;
          break;
        }
        if (result.status === 'discover_failed') throw new Error(result.error || 'ONVIF 검색 실패');
      }
      if (!finished) throw new Error('검색 응답 시간 초과. 현장 검색 프로그램을 확인하세요.');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'ONVIF 검색 실패');
    } finally {
      setOnvifSearching(false);
    }
  };

  const onvifEntity = (device) => {
    const hex = device.ip.split('.').map(part => Number(part).toString(16).padStart(2, '0')).join('');
    return { entity_id: `camera.onvif_${hex}`, entity_name: device.name || `ONVIF ${device.ip}`, domain: 'camera' };
  };

  // ONVIF 엔티티 ID에는 검색 당시 IP가 8자리 16진수로 보존된다.
  // 새로고침 이후에도 계정 등록 UI를 유지하기 위해 IP를 복원한다.
  const getOnvifIp = (entityId) => {
    if (onvifCameraIps[entityId]) return onvifCameraIps[entityId];
    const match = /^camera\.onvif_([0-9a-f]{8})$/i.exec(entityId || '');
    if (!match) return '';
    return (match[1].match(/.{2}/g) || []).map(part => parseInt(part, 16)).join('.');
  };

  const registerCameraAuto = async (entity) => {
    const config = cameraConfigs[entity.entity_id] || {};
    setCameraConfigBusy(entity.entity_id);
    setError('');
    try {
      let rtspUrl = String(config.rtsp_url || '').trim();
      let streamName = String(config.stream_name || '').trim();
      const ip = getOnvifIp(entity.entity_id);
      if (ip) {
        const username = String(config.rtsp_username || '').trim();
        const password = String(config.rtsp_password || '');
        const path = String(config.rtsp_path === '__custom__' ? config.rtsp_custom_path || '' : config.rtsp_path || 'stream1').trim().replace(/^\/+/, '');
        if (!username || !password) throw new Error('카메라 사용자 아이디와 비밀번호를 입력하세요.');
        if (!path || /[?#\s]/.test(path)) throw new Error('올바른 RTSP 스트림 경로를 입력하세요.');
        if (!/^(?:\d{1,3}\.){3}\d{1,3}$/.test(ip)) throw new Error('검색된 카메라 IP가 올바르지 않습니다.');
        rtspUrl = `rtsp://${encodeURIComponent(username)}:${encodeURIComponent(password)}@${ip}:554/${path}`;
        streamName = streamName || `onvif_${ip.replace(/\./g, '_')}`;
      }
      if (!streamName) throw new Error('go2rtc 스트림 이름을 입력하세요.');
      if (!rtspUrl) throw new Error('카메라 RTSP 주소를 입력하세요.');
      const { data } = await api.post('/api/seller/smartfarm/cctv-register', {
        site_id: siteId, entity_id: entity.entity_id,
        camera_name: entity.entity_name || entity.entity_id,
        stream_name: streamName, rtsp_url: rtspUrl,
      });
      if (!data?.ok) throw new Error(data?.error || '등록 요청 실패');
      setCameraConfigs(prev => ({...prev, [entity.entity_id]: {...prev[entity.entity_id], rtsp_url: '', rtsp_password: ''}}));
      setCameraJobStatus(prev => ({...prev, [entity.entity_id]: '현장 등록 중...'}));
      let complete = false;
      for (let i = 0; i < 18; i++) {
        await new Promise(resolve => setTimeout(resolve, 3000));
        const status = (await api.get(`/api/seller/smartfarm/cctv-register/${data.job_id}`)).data;
        if (status.status === 'done') {
          setCameraJobStatus(prev => ({...prev, [entity.entity_id]: '등록 완료'}));
          setNotice('CCTV 자동 등록 완료');
          await loadCameraConfigs();
          setCctvDraftIds(prev => prev.filter(id => id !== entity.entity_id));
          complete = true;
          break;
        }
        if (status.status === 'failed') throw new Error(status.error || '현장 등록 실패');
      }
      if (!complete) setCameraJobStatus(prev => ({...prev, [entity.entity_id]: '등록 처리 중. 잠시 후 다시 확인하세요.'}));
    } catch (err) {
      setCameraConfigs(prev => ({...prev, [entity.entity_id]: {...prev[entity.entity_id], rtsp_password: ''}}));
      setError(err?.response?.data?.error || err?.message || 'CCTV 자동 등록 실패');
    } finally { setCameraConfigBusy(''); }
  };

  const handleEntityToggle = (entityId) => {
    setEntities((prev) =>
      prev.map((entity) =>
        entity.entity_id === entityId
          ? { ...entity, selected: !entity.selected }
          : entity
      )
    );
  };

  const handleEntityNameChange = (entityId, value) => {
    setEntities((prev) =>
      prev.map((entity) =>
        entity.entity_id === entityId
          ? { ...entity, entity_name: value }
          : entity
      )
    );
  };

  const handleVisibleSelectAll = (checked) => {
    const visibleIds = new Set(
      visibleEntities.map((entity) => entity.entity_id)
    );

    setEntities((prev) =>
      prev.map((entity) =>
        visibleIds.has(entity.entity_id)
          ? { ...entity, selected: checked }
          : entity
      )
    );
  };

  const buildSelectedEntitiesPayload = () =>
    entities
      .filter((entity) => entity.selected && !isOnvifCctvEntity(entity.entity_id))
      .map((entity, index) => ({
        entity_id: entity.entity_id,
        entity_name:
          entity.entity_name?.trim() ||
          entity.entity_id,
        entity_type: entity.entity_type || '',
        domain: entity.domain || '',
        icon: entity.icon || '',
        sort_order: index,
        is_visible: 'Y',
      }));

  const handleSaveEntities = async () => {
    if (!isEdit || siteId <= 0) {
      setError('농장 정보를 먼저 저장하세요.');
      return;
    }

    setError('');
    setNotice('');
    setDashboardResult(null);
    setEntitySaving(true);

    try {
      const selectedEntities = buildSelectedEntitiesPayload();

      const response = await api.post(
        '/api/seller/smartfarm/entities-save',
        {
          site_id: siteId,
          entities: selectedEntities,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '엔티티 저장에 실패했습니다.');
      }

      setNotice(
        data.message ||
        `${selectedEntities.length}개의 엔티티가 저장되었습니다.`
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        '엔티티 저장에 실패했습니다.'
      );
    } finally {
      setEntitySaving(false);
    }
  };

  const handleCreateDashboard = async () => {
    if (!isEdit || siteId <= 0) {
      setError('농장 정보를 먼저 저장하세요.');
      return;
    }

    const selectedEntities = buildSelectedEntitiesPayload();

    if (selectedEntities.length === 0) {
      setError('대시보드에 표시할 기기를 하나 이상 선택하세요.');
      return;
    }

    setError('');
    setNotice('');
    setDashboardResult(null);
    setDashboardCreating(true);

    try {
      // 현재 화면의 선택 상태를 먼저 DB에 저장한다.
      const saveResponse = await api.post(
        '/api/seller/smartfarm/entities-save',
        {
          site_id: siteId,
          entities: selectedEntities,
        }
      );

      const saveData = saveResponse.data || {};

      if (!saveData.ok) {
        throw new Error(saveData.error || '선택 기기 저장에 실패했습니다.');
      }

      // 저장된 엔티티를 기준으로 HA Lovelace 대시보드를 생성/갱신한다.
      const response = await api.post(
        '/api/seller/smartfarm/dashboard/create',
        {
          site_id: siteId,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(data.error || '스마트팜 대시보드 생성에 실패했습니다.');
      }

      setDashboardResult({
        url: data.dashboard_url || '',
        urlPath: data.dashboard_url_path || '',
        entityCount: Number(data.entity_count || selectedEntities.length),
        created: Boolean(data.created),
      });

      setNotice(
        data.message ||
        '표준 스마트팜 대시보드가 적용되었습니다.'
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        '스마트팜 대시보드 생성에 실패했습니다.'
      );
    } finally {
      setDashboardCreating(false);
    }
  };

  const handleEsp32DirectRegister = async () => {
    setError('');
    setNotice('');

    if (!form.member_id) {
      setError('회원을 선택하세요.');
      return;
    }

    if (!form.site_name.trim()) {
      setError('농장명을 입력하세요.');
      return;
    }

    if (isEdit && siteId > 0) {
      navigate(`/seller/smartfarm/esp32/builder?site_id=${siteId}`);
      return;
    }

    setSaving(true);

    try {
      const response = await api.post(
        '/api/seller/smartfarm/save-process',
        {
          member_id: String(form.member_id || '').trim(),
          site_name: form.site_name.trim(),
          connection_type: 'DIRECT',
          ha_base_url: '',
          ha_token: '',
          is_active: form.is_active,
        }
      );

      const data = response.data || {};

      if (!data.ok) {
        throw new Error(
          data.error ||
          'ESP32 직접 연결 농장 등록에 실패했습니다.'
        );
      }

      const savedId = Number(data.id || 0);

      if (savedId <= 0) {
        throw new Error('등록된 농장의 site_id를 확인할 수 없습니다.');
      }

      navigate(
        `/seller/smartfarm/esp32/builder?site_id=${savedId}`,
        {
          replace: true,
        }
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
        err?.message ||
        'ESP32 직접 연결 농장 등록에 실패했습니다.'
      );
    } finally {
      setSaving(false);
    }
  };

  const connectionBadge = () => {
    if (!connectionResult) {
      return (
        <span className="badge rounded-pill text-bg-light border text-secondary">
          연결 확인 전
        </span>
      );
    }

    if (connectionResult.connected) {
      return (
        <span className="badge rounded-pill text-bg-success">
          연결 성공
        </span>
      );
    }

    return (
      <span className="badge rounded-pill text-bg-danger">
        연결 실패
      </span>
    );
  };

  const mycodoConnectionBadge = () => {
    if (!mycodoConnectionResult) {
      return (
        <span className="badge rounded-pill text-bg-light border text-secondary">
          연결 확인 전
        </span>
      );
    }

    if (mycodoConnectionResult.connected) {
      return (
        <span className="badge rounded-pill text-bg-success">
          연결 성공
        </span>
      );
    }

    return (
      <span className="badge rounded-pill text-bg-danger">
        연결 실패
      </span>
    );
  };

  if (pageLoading) {
    return (
      <div className="py-5 text-center">
        <div
          className="spinner-border text-primary"
          role="status"
          aria-label="로딩 중"
        />
        <div className="mt-3 text-secondary">
          스마트팜 정보를 불러오고 있습니다.
        </div>
      </div>
    );
  }

  return (
  <SellerLayout>
    <div className="container-fluid px-0">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1 fw-bold">
            {isEdit ? '스마트팜 수정' : '스마트팜 등록'}
          </h2>
          <p className="mb-0 text-secondary">
            {shopName ? `${shopName} · ` : ''}
            회원 농장의 Home Assistant, Mycodo 자동화 및 ESP32 연결 정보를 관리합니다.
          </p>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary text-nowrap"
          onClick={() => navigate('/seller/smartfarm')}
        >
          목록으로
        </button>
      </div>

      {error && (
        <div
          className="alert alert-danger d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setError('')}
          />
        </div>
      )}

      {notice && (
        <div
          className="alert alert-success d-flex justify-content-between align-items-center"
          role="alert"
        >
          <span>{notice}</span>
          <button
            type="button"
            className="btn-close"
            aria-label="닫기"
            onClick={() => setNotice('')}
          />
        </div>
      )}

      {updateModalOpen && (
        <div
          className="modal d-block"
          role="dialog"
          aria-modal="true"
          aria-labelledby="smartfarm-update-title"
          style={{ backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1055 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setUpdateModalOpen(false);
          }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <div>
                  <h5 className="modal-title fw-bold" id="smartfarm-update-title">시스템 업데이트</h5>
                  <div className="small text-secondary mt-1">
                    {form.site_name || `농장 #${siteId}`} · 농장 ID {siteId}
                  </div>
                </div>
                <button type="button" className="btn-close" aria-label="닫기" onClick={() => setUpdateModalOpen(false)} />
              </div>
              <div className="modal-body">
                <div className="alert alert-info mb-3" role="status">
                  {updateStatus?.online ? '현장 업데이트 에이전트 온라인' : '업데이트 에이전트 미설치 또는 오프라인'}
                </div>
                {!updateStatus?.online && <button type="button" className="btn btn-primary mb-3"
                  disabled={updatePairBusy || (!physicalCctvUrl && !updatePendingActivation)} onClick={registerUpdateWorker}>
                  {updatePairBusy ? '자동 인증 중...' : updatePendingActivation ? '업데이트 Worker 인증 재시도' : updateStatus?.agent_installed ? '업데이트 Worker 재등록' : '업데이트 Worker 자동 등록'}
                </button>}
                <div className="table-responsive">
                  <table className="table align-middle mb-0">
                    <thead><tr><th>구성 요소</th><th>설치 버전</th><th>상태</th><th className="text-end">작업</th></tr></thead>
                    <tbody>
                      <tr>
                        <td className="fw-semibold">Cloudflare Tunnel</td>
                        <td>{updateStatus?.installed_version || '조회 대기'}</td>
                        <td>{updateStatus?.job?.status || (updateStatus?.online ? '대기' : '오프라인')}
                          {updateStatus?.job?.error_message && <div className="small text-danger">{updateStatus.job.error_message}</div>}
                        </td>
                        <td className="text-end"><button type="button" className="btn btn-sm btn-outline-primary"
                          disabled={updateBusy || !updateStatus?.online || updateStatus?.installed_version === updateStatus?.target_version || ['pending','running'].includes(updateStatus?.job?.status)}
                          onClick={requestCloudflaredUpdate}>업데이트</button></td>
                      </tr>
                      {['Mycodo', 'Home Assistant', 'CCTV Worker'].map(name => (
                        <tr key={name}><td>{name}</td><td>조회 미연결</td><td>추후 지원</td><td className="text-end"><button type="button" className="btn btn-sm btn-outline-secondary" disabled>업데이트</button></td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="small text-secondary mt-3">
                  업데이트 실행 시에는 사전 백업, 대상 서비스만 재시작, 정상 동작 검증 및 실패 시 롤백 기능을 연결할 예정입니다.
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setUpdateModalOpen(false)}>닫기</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3">
            <div className="fw-bold">농장 기본 정보</div>
            <div className="small text-secondary mt-1">
              스마트팜을 사용할 회원과 농장명을 선택합니다.
            </div>
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12">
                <label
                  htmlFor="memberSearch"
                  className="form-label fw-semibold"
                >
                  회원 검색
                </label>
                <input
                  id="memberSearch"
                  type="search"
                  className="form-control"
                  value={memberSearch}
                  onChange={(event) =>
                    setMemberSearch(event.target.value)
                  }
                  placeholder="회원명, 이메일, 전화번호 검색"
                />
              </div>

              <div className="col-12 col-lg-6">
              <label
                htmlFor="member_id"
                className="form-label fw-semibold"
              >
                회원 <span className="text-danger">*</span>
              </label>

              <select
                id="member_id"
                name="member_id"
                className="form-select"
                value={form.member_id || ''}
                onChange={handleChange}
                required
              >
                <option value="">회원을 선택하세요</option>

                {filteredMembers.map((member) => {
                  const memberId = String(
                    member.member_id ?? member.id ?? ''
                  ).trim();

                  return (
                    <option
                      key={memberId}
                      value={memberId}
                    >
                      {member.nickname || memberId || '이름 없음'}
                    </option>
                  );
                })}
              </select>

                <div className="form-text">
                  검색 결과 {filteredMembers.length}명
                </div>
              </div>

              <div className="col-12 col-lg-6">
                <label
                  htmlFor="site_name"
                  className="form-label fw-semibold"
                >
                  농장명 <span className="text-danger">*</span>
                </label>

                <input
                  id="site_name"
                  name="site_name"
                  type="text"
                  className="form-control"
                  value={form.site_name}
                  onChange={handleChange}
                  maxLength={100}
                  placeholder="예: 김철수 딸기농장"
                  required
                />
              </div>

              <div className="col-12">
                <label className="form-label fw-semibold">
                  연결 방식
                </label>

                <div className="d-flex flex-column flex-sm-row gap-2">
                  <button
                    type="button"
                    className={`btn ${
                      connectionPanel === 'HA'
                        ? 'btn-primary'
                        : 'btn-outline-primary'
                    }`}
                    onClick={() => {
                      setConnectionType('HA');
                      setConnectionPanel('HA');
                      setConnectionResult(null);
                    }}
                  >
                    Home Assistant 연결
                  </button>

                  <button
                    type="button"
                    className={`btn ${
                      connectionPanel === 'MYCODO'
                        ? 'btn-warning'
                        : 'btn-outline-warning'
                    }`}
                    onClick={() => {
                      setConnectionType('HA');
                      setConnectionPanel('MYCODO');
                      setMycodoConnectionResult(null);
                    }}
                  >
                    Mycodo 연결
                  </button>

                  <button
                    type="button"
                    className={`btn ${
                      connectionPanel === 'CCTV'
                        ? 'btn-info'
                        : 'btn-outline-info'
                    }`}
                    onClick={() => {
                      // CCTV is an independent panel, not a DB connection_type.
                      setConnectionPanel('CCTV');
                      setError('');
                    }}
                  >
                    CCTV 연결
                  </button>

                  <button
                    type="button"
                    className={`btn ${
                      connectionPanel === 'DIRECT'
                        ? 'btn-success'
                        : 'btn-outline-success'
                    }`}
                    onClick={() => {
                      setConnectionType('DIRECT');
                      setConnectionPanel('DIRECT');
                      setConnectionResult(null);
                      setMycodoConnectionResult(null);
                    }}
                  >
                    ESP32 직접 연결
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      if (!isEdit || siteId <= 0) {
                        setError('농장 정보를 먼저 저장한 후 스마트팜 2D팜을 열어주세요.');
                        return;
                      }

                      window.open(
                        `/seller/smartfarm/2d?site_id=${siteId}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    스마트팜 2D팜
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={() => {
                      if (!isEdit || siteId <= 0) {
                        setError('농장 정보를 먼저 저장한 후 콩꼬물 3D팜을 열어주세요.');
                        return;
                      }

                      window.open(
                        `/seller/smartfarm/3d?site_id=${siteId}`,
                        '_blank',
                        'noopener,noreferrer'
                      );
                    }}
                  >
                    콩꼬물 3D팜
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => {
                      if (!isEdit || siteId <= 0) {
                        setError('농장 정보를 먼저 저장한 후 시스템 업데이트를 열어주세요.');
                        return;
                      }
                      setUpdateModalOpen(true);
                    }}
                  >
                    시스템 업데이트
                  </button>
                </div>

                <div className="form-text">
                  Home Assistant는 기본 기기 연결, Mycodo는 자동화 연동, ESP32 직접 연결은 독립 등록에 사용합니다.
                </div>
              </div>

              <div className="col-12">
                <div className="form-check form-switch">
                  <input
                    id="is_active"
                    className="form-check-input"
                    type="checkbox"
                    checked={form.is_active === 'Y'}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        is_active: event.target.checked ? 'Y' : 'N',
                      }))
                    }
                  />
                  <label
                    className="form-check-label fw-semibold"
                    htmlFor="is_active"
                  >
                    회원 스마트팜 사용 허용
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        {connectionType === 'HA' && connectionPanel === 'HA' && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <div className="fw-bold">
                Home Assistant 연결
              </div>
              <div className="small text-secondary mt-1">
                해당 농장에 설치된 Home Assistant 주소와 토큰을 입력합니다.
              </div>
            </div>

            {connectionBadge()}
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12">
                <label
                  htmlFor="ha_base_url"
                  className="form-label fw-semibold"
                >
                  HA 주소 <span className="text-danger">*</span>
                </label>

                <input
                  id="ha_base_url"
                  name="ha_base_url"
                  type="url"
                  className="form-control"
                  value={form.ha_base_url}
                  onChange={handleChange}
                  placeholder="예: https://farm.example.com 또는 http://192.168.0.20:8123"
                  required
                />

                <div className="form-text">
                  주소 마지막의 /는 입력하지 않아도 됩니다.
                </div>
              </div>

              <div className="col-12">
                <label
                  htmlFor="ha_token"
                  className="form-label fw-semibold"
                >
                  Long-Lived Access Token
                  {!isEdit && (
                    <span className="text-danger"> *</span>
                  )}
                </label>

                <textarea
                  id="ha_token"
                  name="ha_token"
                  className="form-control font-monospace"
                  rows={4}
                  value={form.ha_token}
                  onChange={handleChange}
                  placeholder={
                    isEdit
                      ? '변경할 때만 새 토큰을 입력하세요.'
                      : 'Home Assistant에서 발급한 Long-Lived Access Token을 입력하세요.'
                  }
                />

                {isEdit && tokenSaved && (
                  <div className="form-text">
                    기존 토큰이 저장되어 있습니다
                    {tokenMasked ? ` (${tokenMasked})` : ''}.
                    빈칸으로 저장하면 기존 토큰을 유지합니다.
                  </div>
                )}
              </div>

              <div className="col-12">
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleTestConnection}
                    disabled={
                      connectionTesting ||
                      saving ||
                      entityLoading ||
                      entitySaving
                    }
                  >
                    {connectionTesting
                      ? '연결 확인 중...'
                      : '연결 테스트'}
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={
                      saving ||
                      connectionTesting ||
                      entityLoading ||
                      entitySaving
                    }
                  >
                    {saving
                      ? '저장 중...'
                      : isEdit
                        ? '농장 정보 저장'
                        : '농장 등록'}
                  </button>
                </div>
              </div>

              {connectionResult && (
                <div className="col-12">
                  <div
                    className={`alert mb-0 ${
                      connectionResult.connected
                        ? 'alert-success'
                        : 'alert-danger'
                    }`}
                  >
                    {connectionResult.message}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {connectionType === 'HA' && connectionPanel === 'MYCODO' && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
            <div>
              <div className="fw-bold">
                Mycodo 자동화 연결
              </div>
              <div className="small text-secondary mt-1">
                해당 농장에 설치된 Mycodo 주소, API Key와 Function ID를 입력합니다.
              </div>
            </div>

            {mycodoConnectionBadge()}
          </div>

          <div className="card-body">
            <div className="row g-4">
              <div className="col-12">
                <label
                  htmlFor="mycodo_base_url"
                  className="form-label fw-semibold"
                >
                  Mycodo 주소
                </label>

                <input
                  id="mycodo_base_url"
                  name="mycodo_base_url"
                  type="url"
                  className="form-control"
                  value={form.mycodo_base_url}
                  onChange={handleChange}
                  placeholder="예: https://mycodo-40.konggomul.com"
                />

                <div className="form-text">
                  자동화 기능을 사용하지 않는 농장은 비워둘 수 있습니다.
                </div>
              </div>

              <div className="col-12">
                <label
                  htmlFor="mycodo_api_key"
                  className="form-label fw-semibold"
                >
                  Mycodo API Key
                </label>

                <textarea
                  id="mycodo_api_key"
                  name="mycodo_api_key"
                  className="form-control font-monospace"
                  rows={4}
                  value={form.mycodo_api_key}
                  onChange={handleChange}
                  placeholder={
                    isEdit
                      ? '변경할 때만 새 API Key를 입력하세요.'
                      : 'Mycodo에서 발급한 API Key를 입력하세요.'
                  }
                />

                {isEdit && mycodoApiKeySaved && (
                  <div className="form-text">
                    기존 Mycodo API Key가 저장되어 있습니다
                    {mycodoApiKeyMasked
                      ? ` (${mycodoApiKeyMasked})`
                      : ''}.
                    빈칸으로 저장하면 기존 API Key를 유지합니다.
                  </div>
                )}
              </div>

              <div className="col-12">
                <label
                  htmlFor="mycodo_function_id"
                  className="form-label fw-semibold"
                >
                  Function ID
                </label>

                <input
                  id="mycodo_function_id"
                  name="mycodo_function_id"
                  type="text"
                  className="form-control font-monospace"
                  value={form.mycodo_function_id}
                  onChange={handleChange}
                  placeholder="예: c4aa4bca-bf95-4d25-a29f-0597a90c495e"
                />

                <div className="form-text">
                  회원 화면의 자동화 시작/중지 버튼이 제어할 Mycodo Function의 unique_id입니다.
                </div>
              </div>

              <div className="col-12">
                <div className="d-flex flex-column flex-sm-row gap-2">
                  <button
                    type="button"
                    className="btn btn-outline-warning"
                    onClick={handleTestMycodoConnection}
                    disabled={
                      mycodoConnectionTesting ||
                      saving ||
                      entityLoading ||
                      entitySaving
                    }
                  >
                    {mycodoConnectionTesting
                      ? '연결 확인 중...'
                      : 'Mycodo 연결 테스트'}
                  </button>

                  <button
                    type="submit"
                    className="btn btn-warning"
                    disabled={
                      saving ||
                      mycodoConnectionTesting ||
                      entityLoading ||
                      entitySaving
                    }
                  >
                    {saving
                      ? '저장 중...'
                      : isEdit
                        ? 'Mycodo 설정 저장'
                        : '농장 등록'}
                  </button>
                </div>
              </div>

              {mycodoConnectionResult && (
                <div className="col-12">
                  <div
                    className={`alert mb-0 ${
                      mycodoConnectionResult.connected
                        ? 'alert-success'
                        : 'alert-danger'
                    }`}
                  >
                    {mycodoConnectionResult.message}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {connectionType === 'DIRECT' && connectionPanel === 'DIRECT' && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white border-0 py-3">
            <div className="fw-bold">
              ESP32 직접 등록
            </div>
            <div className="small text-secondary mt-1">
              Home Assistant를 거치지 않고 ESP32 펌웨어 설정 페이지로 이동합니다.
            </div>
          </div>

          <div className="card-body">
            <div className="d-flex flex-column flex-sm-row gap-2 align-items-sm-center justify-content-between">
              <div className="small text-secondary">
                {isEdit
                  ? `현재 농장 Site ID: ${siteId}`
                  : '회원과 농장명을 확인한 후 ESP32를 직접 등록할 수 있습니다.'}
              </div>

              <button
                type="button"
                className="btn btn-success text-nowrap"
                onClick={handleEsp32DirectRegister}
                disabled={saving}
              >
                {saving
                  ? '등록 중...'
                  : 'ESP32 직접 등록'}
              </button>
            </div>
          </div>
        </div>
        )}
      </form>

      {connectionType === 'HA' && connectionPanel === 'HA' && (
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header bg-white border-0 py-3 d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-3">
          <div>
            <div className="fw-bold">
              회원에게 보여줄 기기
            </div>
            <div className="small text-secondary mt-1">
              Home Assistant 엔티티를 불러온 후 필요한 항목만 선택합니다.
            </div>
          </div>

          <div className="d-flex flex-column flex-sm-row gap-2">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleLoadEntities}
              disabled={
                !isEdit ||
                entityLoading ||
                saving ||
                entitySaving ||
                dashboardCreating
              }
            >
              {entityLoading
                ? '엔티티 불러오는 중...'
                : '엔티티 다시 불러오기'}
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveEntities}
              disabled={
                !isEdit ||
                entitySaving ||
                entityLoading ||
                saving ||
                dashboardCreating
              }
            >
              {entitySaving
                ? '기기 저장 중...'
                : `선택 기기 저장 (${selectedCount})`}
            </button>

            <button
              type="button"
              className="btn btn-success"
              onClick={handleCreateDashboard}
              disabled={
                !isEdit ||
                selectedCount === 0 ||
                dashboardCreating ||
                entitySaving ||
                entityLoading ||
                saving
              }
            >
              {dashboardCreating
                ? '대시보드 적용 중...'
                : '스마트팜 대시보드 생성/갱신'}
            </button>
          </div>
        </div>

        {dashboardResult && (
          <div className="card-body border-top bg-light">
            <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2">
              <div>
                <div className="fw-semibold text-success">
                  표준 스마트팜 대시보드 적용 완료
                </div>
                <div className="small text-secondary mt-1">
                  {dashboardResult.entityCount}개 기기 · {dashboardResult.created ? '신규 생성' : '기존 대시보드 갱신'}
                </div>
              </div>

              {dashboardResult.url && (
                <a
                  className="btn btn-outline-success text-nowrap"
                  href={dashboardResult.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  Home Assistant에서 열기
                </a>
              )}
            </div>
          </div>
        )}

        {!isEdit ? (
          <div className="card-body py-5 text-center">
            <div className="fw-semibold mb-2">
              농장 정보를 먼저 저장하세요.
            </div>
            <div className="text-secondary">
              저장 후 Home Assistant 엔티티를 불러올 수 있습니다.
            </div>
          </div>
        ) : entities.length === 0 ? (
          <div className="card-body py-5 text-center">
            <div className="fw-semibold mb-2">
              불러온 엔티티가 없습니다.
            </div>
            <div className="text-secondary mb-3">
              연결 상태를 확인하고 엔티티 다시 불러오기를 누르세요.
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={handleLoadEntities}
              disabled={entityLoading}
            >
              {entityLoading
                ? '불러오는 중...'
                : '엔티티 불러오기'}
            </button>
          </div>
        ) : (
          <>
            <div className="card-body border-top">
              <div className="row g-2">
                <div className="col-12 col-lg">
                  <input
                    type="search"
                    className="form-control"
                    value={entitySearch}
                    onChange={(event) =>
                      setEntitySearch(event.target.value)
                    }
                    placeholder="기기명 또는 entity_id 검색"
                  />
                </div>

                <div className="col-12 col-sm-6 col-lg-3">
                  <select
                    className="form-select"
                    value={domainFilter}
                    onChange={(event) =>
                      setDomainFilter(event.target.value)
                    }
                  >
                    <option value="ALL">전체 종류</option>
                    {domains.map((domain) => (
                      <option
                        key={domain}
                        value={domain}
                      >
                        {domain}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="col-6 col-sm-3 col-lg-auto">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => handleVisibleSelectAll(true)}
                  >
                    전체 선택
                  </button>
                </div>

                <div className="col-6 col-sm-3 col-lg-auto">
                  <button
                    type="button"
                    className="btn btn-outline-secondary w-100"
                    onClick={() => handleVisibleSelectAll(false)}
                  >
                    선택 해제
                  </button>
                </div>
              </div>

              <div className="small text-secondary mt-3">
                표시 {visibleEntities.length}개 · 선택 {selectedCount}개
              </div>
            </div>

            <div className="table-responsive d-none d-lg-block">
              <table className="table align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th
                      className="text-center"
                      style={{ width: 70 }}
                    >
                      표시
                    </th>
                    <th style={{ minWidth: 220 }}>표시 이름</th>
                    <th style={{ minWidth: 260 }}>Entity ID</th>
                    <th style={{ width: 130 }}>종류</th>
                    <th style={{ width: 140 }}>현재 상태</th>
                  </tr>
                </thead>

                <tbody>
                  {visibleEntities.map((entity) => (
                    <tr key={entity.entity_id}>
                      <td className="text-center">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={Boolean(entity.selected)}
                          onChange={() =>
                            handleEntityToggle(entity.entity_id)
                          }
                          aria-label={`${entity.entity_name} 표시`}
                        />
                      </td>

                      <td>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={entity.entity_name}
                          onChange={(event) =>
                            handleEntityNameChange(
                              entity.entity_id,
                              event.target.value
                            )
                          }
                          disabled={!entity.selected}
                        />
                      </td>

                      <td>
                        <code className="small text-break">
                          {entity.entity_id}
                        </code>
                      </td>

                      <td>
                        <span className="badge text-bg-light border text-dark">
                          {entity.domain || '-'}
                        </span>
                      </td>

                      <td>
                        <span className="fw-medium">
                          {entity.state ?? '-'}
                        </span>
                        {entity.unit_of_measurement && (
                          <span className="text-secondary ms-1">
                            {entity.unit_of_measurement}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-lg-none">
              {visibleEntities.map((entity) => (
                <div
                  key={entity.entity_id}
                  className="border-top p-3"
                >
                  <div className="d-flex align-items-start gap-3">
                    <input
                      className="form-check-input mt-1"
                      type="checkbox"
                      checked={Boolean(entity.selected)}
                      onChange={() =>
                        handleEntityToggle(entity.entity_id)
                      }
                      aria-label={`${entity.entity_name} 표시`}
                    />

                    <div className="flex-grow-1 min-w-0">
                      <input
                        type="text"
                        className="form-control form-control-sm mb-2"
                        value={entity.entity_name}
                        onChange={(event) =>
                          handleEntityNameChange(
                            entity.entity_id,
                            event.target.value
                          )
                        }
                        disabled={!entity.selected}
                      />

                      <div className="small text-break mb-2">
                        <code>{entity.entity_id}</code>
                      </div>

                      <div className="d-flex flex-wrap gap-2 align-items-center">
                        <span className="badge text-bg-light border text-dark">
                          {entity.domain || '-'}
                        </span>

                        <span className="small text-secondary">
                          상태: {entity.state ?? '-'}
                          {entity.unit_of_measurement
                            ? ` ${entity.unit_of_measurement}`
                            : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {visibleEntities.length === 0 && (
              <div className="py-5 text-center text-secondary border-top">
                검색 조건에 맞는 엔티티가 없습니다.
              </div>
            )}
          </>
        )}
      </div>
      )}

      {isEdit && connectionPanel === 'CCTV' && (
        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white fw-bold">CCTV go2rtc 연결 설정</div>
          <div className="card-body">
            <p className="small text-secondary">
              ONVIF 검색으로 발견한 카메라를 선택해 등록할 수 있습니다.
              기존 CCTV 등록 정보와 영상 재생 방식은 유지됩니다.
            </p>
            <div className="border rounded p-3 mb-3">
              <div className="fw-semibold mb-2">현장 CCTV 연결 {cctvPaired ? (cctvOnline ? '(온라인)' : '(연결 저장됨 · 접속 대기)') : '(최초 1회 설정)'}</div>
              <p className="small text-secondary mb-2">HA/Mycodo의 사이트 번호와 CCTV Worker 번호를 일치시킵니다. 기존 Worker 교체 시 새 Worker 활성화가 성공한 후 중앙 연결을 전환합니다.</p>
              <div className="row g-2">
                <div className="col-12 col-md-6">
                  <label className="form-label small">CCTV 공개 주소 (HA/Mycodo 주소에서 자동 결정)</label>
                  <input className="form-control" value={physicalCctvUrl || 'HA/Mycodo 주소 확인 필요'} readOnly />
                </div>
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-primary"
                    disabled={cctvPairBusy || (!physicalCctvUrl && !cctvPendingActivation)}
                    onClick={issueCctvWorkerToken}>{cctvPairBusy ? '처리 중...' : cctvPendingActivation ? 'CCTV 활성화 재시도' : cctvPaired ? '새 CCTV Worker로 교체' : 'CCTV Worker 자동 등록'}</button>
                </div>
              </div>
              {!cctvPaired && (
                <details className="mt-3">
                  <summary className="small">이미 발급받은 토큰으로 수동 연결</summary>
                  <div className="d-flex flex-wrap gap-2 mt-2">
                    <input className="form-control" style={{maxWidth: '440px'}} type="password" autoComplete="new-password"
                      value={cctvPair.agent_token} placeholder="기존 Worker 인증 토큰"
                      onChange={e => setCctvPair(prev => ({...prev, agent_token: e.target.value}))} />
                    <button type="button" className="btn btn-outline-primary" disabled={cctvPairBusy || !cctvPair.agent_token}
                      onClick={pairCctvWorker}>{cctvPairBusy ? '연결 중...' : '현장 연결 저장'}</button>
                  </div>
                </details>
              )}
            </div>
            <div className="border rounded p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <strong>ONVIF 카메라 자동 검색</strong>
                <button type="button" className="btn btn-outline-primary btn-sm"
                  disabled={!cctvPaired || onvifSearching} onClick={searchOnvifCameras}>
                  {onvifSearching ? '검색 중...' : 'ONVIF 자동 검색'}
                </button>
              </div>
              <div className="small text-secondary mb-2">검색된 카메라를 선택하고 카메라 계정 아이디·비밀번호를 입력하세요. IP와 CCTV 공개 주소는 자동으로 설정됩니다.</div>
              {onvifDevices.map((device) => (
                <button key={device.ip} type="button" className="btn btn-outline-secondary btn-sm me-2 mb-2"
                  onClick={() => {
                    const entity = onvifEntity(device);
                    setOnvifDevices(prev => prev.filter(item => item.ip !== device.ip));
                    setCctvDraftIds(prev => prev.includes(entity.entity_id) ? prev : [...prev, entity.entity_id]);
                    setOnvifCameraIps(prev => ({...prev, [entity.entity_id]: device.ip}));
                    setCameraConfigs(prev => ({ ...prev, [entity.entity_id]: {
                      ...prev[entity.entity_id], camera_name: entity.entity_name, stream_name: prev[entity.entity_id]?.stream_name || `onvif_${device.ip.replace(/\./g, '_')}`, rtsp_path: prev[entity.entity_id]?.rtsp_path || 'stream1'
                    }}));
                  }}>{device.name} ({device.ip}) 선택</button>
              ))}
            </div>
            {Object.keys(cameraConfigs).filter(id => cameraConfigs[id]?.public_url).map(id => ({
              entity_id: id, domain: 'camera',
              entity_name: cameraConfigs[id].camera_name || entities.find(e => e.entity_id === id)?.entity_name || id,
            })).concat(cctvDraftIds.filter(id => !cameraConfigs[id]?.public_url).map(id =>
              ({ entity_id: id, entity_name: cameraConfigs[id]?.camera_name || id, domain: 'camera' })
            )).map((entity) => {
              const config = cameraConfigs[entity.entity_id] || {};
              const onvifIp = getOnvifIp(entity.entity_id);
              const setConfig = (field, value) => setCameraConfigs((prev) => ({
                ...prev,
                [entity.entity_id]: { ...prev[entity.entity_id], [field]: value },
              }));
              return (
                <div key={entity.entity_id} className="border rounded p-3 mb-3">
                  <div className="fw-semibold mb-2">{entity.entity_name} <span className="small text-secondary">({entity.entity_id})</span></div>
                  {Boolean(config.public_url) && (
                    <div className="row g-2 mb-2">
                      <div className="col-12 col-md-9">
                        <label className="form-label small">사용자 화면 표시 이름 (연결 설정과 무관)</label>
                        <input className="form-control" type="text" maxLength={100}
                          value={config.display_name || ''}
                          placeholder={config.camera_name || entity.entity_id}
                          onChange={(e) => setConfig('display_name', e.target.value)} />
                      </div>
                      <div className="col-12 col-md-3 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-primary w-100"
                          disabled={Boolean(cameraConfigBusy)}
                          onClick={() => saveCameraDisplayName(entity.entity_id)}>
                          표시 이름 저장
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="row g-2">
                    {onvifIp ? (
                      <>
                        <div className="col-12">
                          <div className="small text-secondary">카메라 IP: {onvifIp} · CCTV 공개 주소: {cctvPair.public_url || physicalCctvUrl || 'Worker 연결 확인 필요'}</div>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label small">카메라 사용자 아이디</label>
                          <input className="form-control" type="text" autoComplete="off"
                            value={config.rtsp_username || ''} placeholder="카메라 계정 아이디"
                            onChange={(e) => setConfig('rtsp_username', e.target.value)} />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label small">카메라 비밀번호</label>
                          <input className="form-control" type="password" autoComplete="new-password"
                            value={config.rtsp_password || ''} placeholder="카메라 계정 비밀번호"
                            onChange={(e) => setConfig('rtsp_password', e.target.value)} />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label small">go2rtc 스트림 이름</label>
                          <input className="form-control" type="text"
                            value={config.stream_name || `onvif_${onvifIp.replace(/\./g, '_')}`}
                            onChange={(e) => setConfig('stream_name', e.target.value)} />
                          <div className="form-text">영상 구분용 이름입니다. 영문, 숫자, 밑줄(_), 하이픈(-)만 사용합니다.</div>
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label small">카메라 영상 스트림</label>
                          <select className="form-select" value={config.rtsp_path || 'stream1'}
                            onChange={(e) => setConfig('rtsp_path', e.target.value)}>
                            <option value="stream1">stream1 · 고화질 (메인 스트림)</option>
                            <option value="stream2">stream2 · 저화질 (보조 스트림)</option>
                            <option value="__custom__">직접 입력 · 다른 카메라 스트림 경로</option>
                          </select>
                          {config.rtsp_path === '__custom__' && (
                            <div className="mt-2">
                              <label className="form-label small">RTSP 스트림 경로 직접 입력</label>
                              <input className="form-control" type="text"
                                value={config.rtsp_custom_path || ''}
                                placeholder="예: Streaming/Channels/101"
                                onChange={(e) => setConfig('rtsp_custom_path', e.target.value)} />
                              <div className="form-text">IP와 포트는 자동으로 붙습니다. 경로만 입력하세요. 앞의 /는 생략할 수 있습니다.</div>
                            </div>
                          )}
                          <div className="form-text">Tapo C110은 stream1/stream2를 선택하세요. 다른 제조사는 직접 입력을 사용하세요.</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="col-12 col-md-4">
                          <label className="form-label small">go2rtc 스트림 이름</label>
                          <input className="form-control" value={config.stream_name || ''}
                            placeholder="tapo_c110" onChange={(e) => setConfig('stream_name', e.target.value)} />
                        </div>
                        <div className="col-12 col-md-6">
                          <label className="form-label small">CCTV 공개 주소</label>
                          <input className="form-control" value={config.public_url || ''}
                            placeholder={cctvPair.public_url || physicalCctvUrl || 'CCTV Worker 연결 필요'}
                            onChange={(e) => setConfig('public_url', e.target.value)} />
                        </div>
                        <div className="col-12">
                          <label className="form-label small">카메라 RTSP 주소 (수동 등록용)</label>
                          <input className="form-control" type="password" autoComplete="off"
                            value={config.rtsp_url || ''} placeholder="rtsp://사용자:비밀번호@192.168.x.x:554/stream1"
                            onChange={(e) => setConfig('rtsp_url', e.target.value)} />
                          <div className="form-text">기존 HA 카메라 수동 등록 방식은 유지됩니다.</div>
                        </div>
                      </>
                    )}
                    <div className="col-12 col-md-2 d-flex align-items-end">
                      <button type="button" className="btn btn-primary w-100"
                        disabled={cameraConfigBusy === entity.entity_id}
                        onClick={() => registerCameraAuto(entity)}>
                        {cameraConfigBusy === entity.entity_id ? '등록 중...' : '자동 등록'}
                      </button>
                    </div>
                    {Boolean(cameraConfigs[entity.entity_id]?.public_url) && (
                      <div className="col-12 col-md-2 d-flex align-items-end">
                        <button type="button" className="btn btn-outline-danger w-100"
                          disabled={cameraConfigBusy === entity.entity_id}
                          onClick={() => deleteCameraConfig(entity)}>등록 해제</button>
                      </div>
                    )}
                    <div className="col-12 small text-secondary">{cameraJobStatus[entity.entity_id] || ''}</div>
                    {!entity.entity_id.startsWith('camera.onvif_') && <div className="col-12 col-md-2 d-flex align-items-end">
                      <button type="button" className="btn btn-outline-primary w-100"
                        disabled={cameraConfigBusy === entity.entity_id}
                        onClick={() => saveCameraConfig(entity)}>
                        {cameraConfigBusy === entity.entity_id ? '저장 중...' : '연결 저장'}
                      </button>
                    </div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 pb-4">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={() => navigate('/seller/smartfarm')}
        >
          취소
        </button>

        {isEdit && connectionType === 'HA' && connectionPanel === 'HA' && (
          <>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSaveEntities}
              disabled={
                entitySaving ||
                entityLoading ||
                saving ||
                dashboardCreating
              }
            >
              {entitySaving
                ? '저장 중...'
                : `선택 기기 저장 (${selectedCount})`}
            </button>

            <button
              type="button"
              className="btn btn-success"
              onClick={handleCreateDashboard}
              disabled={
                selectedCount === 0 ||
                dashboardCreating ||
                entitySaving ||
                entityLoading ||
                saving
              }
            >
              {dashboardCreating
                ? '대시보드 적용 중...'
                : '대시보드 생성/갱신'}
            </button>
          </>
        )}
      </div>
    </div>
  </SellerLayout>
  );
};

export default SellerSmartfarmForm;
